"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = require("react");
const swr_1 = require("swr");
const useFetch_1 = __importDefault(require("./useFetch"));
const urls_1 = require("../../lib/urls");
const useDeepMemo_1 = require("../useDeepMemo");
const useArrayOrObject_1 = require("../useArrayOrObject");
const useUrlParams_1 = require("../useUrlParams");
function injectPageIntoFetchProps(props, page) {
    var _a;
    const method = (_a = props === null || props === void 0 ? void 0 : props.method) !== null && _a !== void 0 ? _a : 'GET';
    let { url } = props;
    let { body } = props;
    if (method === 'GET') {
        url = urls_1.createUrl(url, { page });
    }
    else {
        body = Object.assign(Object.assign({}, (body !== null && body !== void 0 ? body : {})), { page });
    }
    return Object.assign(Object.assign({}, props), { url,
        body });
}
function clearPages(cache, swrKey, lastFetchedPage, mutate) {
    const promises = [];
    if (!swrKey) {
        return;
    }
    for (let i = 1; i <= lastFetchedPage; i++) {
        const pageProps = injectPageIntoFetchProps(swrKey, i);
        promises.push(mutate(pageProps, undefined, false));
    }
    return Promise.all(promises);
}
/* If we are on page 4, we grab all previous pages (1,2,3) and append the data to one big collection. */
function usePageMergedState(lastFetchedPage = 0, swrKey, props) {
    const { cache, mutate } = swr_1.useSWRConfig();
    let runtimeAllData = [];
    if (swrKey) {
        for (let i = 1; i <= lastFetchedPage; i++) {
            const pageProps = injectPageIntoFetchProps(swrKey, i);
            const serializedMapKey = swr_1.unstable_serialize(pageProps);
            const paginateResponse = cache.get(serializedMapKey);
            if (paginateResponse) {
                runtimeAllData = [...runtimeAllData, ...paginateResponse.data];
            }
        }
    }
    const allData = useDeepMemo_1.useDeepMemo(runtimeAllData);
    const setPageData = react_1.useCallback((action) => __awaiter(this, void 0, void 0, function* () {
        var _a;
        if (!swrKey) {
            return;
        }
        const pageProps = injectPageIntoFetchProps(swrKey, 1);
        const serializedMapKey = swr_1.unstable_serialize(pageProps);
        const paginateResponse = cache.get(serializedMapKey);
        const perPage = (_a = paginateResponse.meta) === null || _a === void 0 ? void 0 : _a.per_page;
        const newData = action instanceof Function ? action(allData) : action;
        // It must be at least ONE page, so we use a math.max, think if we set an empty array, newData.length would be actually 0.
        const newPageCnt = Math.max(1, Math.ceil(newData.length / perPage));
        for (let i = 1; i <= newPageCnt; i++) {
            const pageProps = injectPageIntoFetchProps(swrKey, i);
            const serializedMapKey = swr_1.unstable_serialize(pageProps);
            const newPaginateResponse = Object.assign(Object.assign({}, cache.get(serializedMapKey)), { data: newData.slice((i - 1) * perPage, i * perPage) });
            // eslint-disable-next-line no-await-in-loop
            yield mutate(pageProps, newPaginateResponse, false);
        }
    }), [allData, cache, mutate, swrKey]);
    return [allData, setPageData];
}
function usePaginate(props) {
    var _a, _b, _c;
    const [urlParams, setUrlParam] = useUrlParams_1.useUrlParams();
    const initialTargetPage = props.pageParam && urlParams[props.pageParam] ? parseInt(urlParams[props.pageParam], 10) : 1;
    const [targetPage, setTargetPage] = react_1.useState(initialTargetPage);
    const [lastFetchedPage, setLastFetchedPage] = react_1.useState(props.initialData ? (_a = props.initialData) === null || _a === void 0 ? void 0 : _a.meta.current_page : undefined);
    const initialRef = react_1.useRef(false);
    const pageParamRef = react_1.useRef(props.pageParam);
    /* If url params change, set the page back to 1. */
    react_1.useEffect(() => {
        /* Skip first mount. */
        if (!initialRef.current) {
            initialRef.current = true;
            return;
        }
        setTargetPage(1);
        if (pageParamRef.current) {
            setUrlParam(pageParamRef.current, 1);
        }
    }, [props.url, setUrlParam]);
    const fetchProps = injectPageIntoFetchProps(Object.assign(Object.assign({}, props), { formatResponse: (rawResponse) => rawResponse, onResponse: (resp) => {
            setTargetPage(resp.meta.current_page);
            setLastFetchedPage(resp.meta.current_page);
            if (props.pageParam) {
                setUrlParam(props.pageParam, resp.meta.current_page);
            }
        } }), targetPage);
    const [paginateResponse, setPaginateResponse, fetcher] = useFetch_1.default(fetchProps);
    const perPage = (_b = paginateResponse === null || paginateResponse === void 0 ? void 0 : paginateResponse.meta) === null || _b === void 0 ? void 0 : _b.per_page;
    const [pageData, setPageData] = usePageMergedState(lastFetchedPage, fetcher.swrKey, props);
    const { cache } = swr_1.useSWRConfig();
    const getDataByPage = react_1.useCallback((page) => {
        if (!fetcher.swrKey) {
            return undefined;
        }
        const pageProps = injectPageIntoFetchProps(fetcher.swrKey, page);
        const serializedMapKey = swr_1.unstable_serialize(pageProps);
        const paginateResponse = cache.get(serializedMapKey);
        return paginateResponse === null || paginateResponse === void 0 ? void 0 : paginateResponse.data;
    }, [cache, fetcher.swrKey]);
    const returnProps = useDeepMemo_1.useDeepMemo({
        status: fetcher.status,
        error: fetcher.error,
        isAllowed: fetcher.isAllowed,
        data: pageData,
        setData: setPageData,
        perPage,
        swrKey: fetcher.swrKey,
        meta: paginateResponse === null || paginateResponse === void 0 ? void 0 : paginateResponse.meta,
        getDataByPage,
        totalRows: (_c = paginateResponse === null || paginateResponse === void 0 ? void 0 : paginateResponse.meta) === null || _c === void 0 ? void 0 : _c.total,
        lastFetchedPage,
        refresh: fetcher.refresh,
        targetPage,
        setTargetPage,
    });
    return useArrayOrObject_1.useArrayOrObject(pageData, setPageData, returnProps, returnProps);
}
exports.default = usePaginate;
